import { useAsync } from '@tiger/hooks';
import { Alert, Form } from 'antd';
import { $Formutil, withForm } from 'react-antd-formutil';
import { useNavigate } from 'react-router-dom';
import { useConfirmModal } from 'app/components/Modal';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import security from 'stores/security';
import API from 'utils/API';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import LogoffBottom from '../../components/LogoffBottom';

interface MarscoCloseProps {
    $formutil: $Formutil<{
        close_confirm: boolean;
    }>;
}

const MarscoClose: React.FC<MarscoCloseProps> = ({ $formutil }) => {
    const [{ data, loading, error }, getReason] = useAsync(() =>
        http
            .get<{
                status: boolean; // 能否提交注销申请
                reason: string; // 不能提交注销申请的原因
            }>(API.CUSTOMER.checkClose(), {
                params: {
                    customer_id: security.currentCustomerId
                }
            })
            .then(res => res.data)
    );

    const navigate = useNavigate();
    const { open } = useConfirmModal();

    const submitClose = async () => {
        try {
            await http.post(API.CUSTOMER.submitClose(), {
                customer_id: security.currentCustomerId
            });

            open({
                content: __(
                    'The account closure request is successfully submitted. You will receive emails once the status is updated. Please do not trade, transfer or deposit into this account. '
                ),
                onOk: () => {
                    navigate('/profile');
                },
                okText: __('Confirm')
            });
        } catch (error) {
            Toast.show(error);
        }
    };

    const handleSubmit = () => {
        const { $invalid, $getFirstError, $batchState } = $formutil;

        if ($invalid) {
            $batchState({
                $touched: true,
                $dirty: true
            });

            Toast.show($getFirstError());
        } else {
            open({
                content: __(
                    'Please notice the account closure cannot be reversed. Are you sure you would like to continue?'
                ),
                okText: __('Confirm'),
                cancelText: __('Cancel'),
                onOk: submitClose
            });
        }
    };

    const renderMain = () => {
        if (!security.isAccountOpened) {
            return (
                <Alert
                    type="error"
                    message={
                        <span
                            dangerouslySetInnerHTML={{
                                __html: printf(
                                    __(
                                        'You have no account now, if you want to cancel the userID, please do it through <a href="%s">here</a>'
                                    ),
                                    '/profile/logoff/delete'
                                )
                            }}
                        />
                    }
                />
            );
        }

        if (loading) {
            return <Loading />;
        }

        if (error) {
            return <ErrorBox error={error} onClick={getReason} />;
        }

        if (data && data.status === false && data.reason) {
            return (
                <Alert
                    type="error"
                    message={
                        <>
                            {Array.isArray(data.reason)
                                ? data.reason.map(item => <div key={item}>{item}</div>)
                                : data.reason}
                        </>
                    }
                />
            );
        }

        return (
            <>
                <ul className="statement-list">
                    <li className="statement-item">
                        {__(
                            'This Statement governs the relationship between Client(“You”)and TradeUP Securities, Inc.(“We”or ”Us”),which is a broker-dealer registered at U.S. Securities and Exchange Commission( “SEC”) and member of the Financial Industry Regulatory Authority (“FINRA”) and the Securities Investor Protection Corporation (“SIPC”) that provides online brokerage services. Your click of the button of “Submit” is deemed as your understanding and acceptation of the Statement to close your account at TradeUP Securities, Inc.'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            '1. Ensure Recent Deposits Have Cleared All recent deposits and wire transfers must have time to clear according to the customer deposit credit and hold periods before your request to close your account can be honored. You should be responsible for the possible losses during the account closure period.'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            '2. Liquidate or Transfer Positions Accounts with positions cannot be closed. You must either liquidate or transfer all positions to another brokerage firm to have your assets transferred out of TradeUP Securities, Inc. Please contact us for transfer instructions. You could view your latest statement to verify that all positions have been liquidated or transferred. Your application for account closure means that you voluntarily give up the assets or debts in your account.'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            '3. Withdraw Funds Please sell all your positions and withdraw the balance back to your bank account. If you do not have any saved bank information (instruction) for an ACH transfer, create one.'
                        )}
                    </li>
                    <li
                        className="statement-item"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                '4. Check Statements and Tax Forms To access activity statements and tax forms, log in to APP and visit the page of Me and open the Live Account menu. This information will only be available for a limited time after your account is closed. If you need assistance, please let us know by contacting our Customer Service through email <a href="mailto:support@tradeup.com">support@tradeup.com</a> for future access to statements and tax forms.'
                            )
                        }}
                    />
                    <li className="statement-item">
                        {__(
                            '5. Record keeping Requirements Please keep in mind that regulations require us to preserve certain books and records. We take the security of all collected account information and data very seriously, and we don’t intend to use this data for anything beyond the fulfillment of our regulatory requirements.'
                        )}
                    </li>
                    <li
                        className="statement-item"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                'You can view the status of your request to close your account by receiving email from our Customer Service. If the above criteria are not met, our system will be unable to process your account closure request. If you need assistance, please let us know by contacting our Customer Service through email <a href="mailto:support@tradeup.com">support@tradeup.com</a>. We’d be sad to see you go, but we hope you’ve enjoyed the commission-free investing experience.'
                            )
                        }}
                    />
                </ul>
                <LogoffBottom
                    errorMsg={__('You need to confirm all the information to continue to the next page')}
                    checkBoxLabel={__(
                        'I confirm that I have read, understand and agree to the above Broker Dealer Record-Keeping Requirements.'
                    )}
                    btnDisabled={!$formutil.$params.close_confirm}
                />
            </>
        );
    };

    return (
        <Form className="logoff-close-root marsco-close-root" onFinish={handleSubmit}>
            {renderMain()}
        </Form>
    );
};

export default withForm(MarscoClose);
